import React from 'react';

import useServerSideRendering from '@hooks/useServerSideRendering';
import { GoogleOAuthProvider, useGoogleOneTapLogin } from '@react-oauth/google';
import { GOOGLE_CLIENT_ID, GOOGLE_ONE_TAP_USE_FEDCM } from '@src/env';
import { GOOGLE_PROVIDER, createLeadWithOauth } from '@utils/backend-api/oauth';

interface GoogleOneTapProps {
  isGdprRequired: boolean;
  newsletterIds: number[];
  redirectBasePath: string;
  returnTo: string;
}

const DISABLED_PATHS = ['/login', '/subscribe'];

// Keep in sync with OneTap::FORM_LOCATION and OneTap::LEAD_TYPE in app/models/concerns/one_tap.rb
const FORM_LOCATION = 'one_tap_sitewide';
const LEAD_TYPE = 'one_tap_lead';

function GoogleOneTapInner({
  isGdprRequired,
  newsletterIds,
  redirectBasePath,
  returnTo,
}: GoogleOneTapProps) {
  const isDisabled = DISABLED_PATHS.includes(window.location.pathname);

  useGoogleOneTapLogin({
    disabled: isDisabled,
    use_fedcm_for_prompt: GOOGLE_ONE_TAP_USE_FEDCM !== 'false',
    cancel_on_tap_outside: true,
    auto_select: false,
    onSuccess: async (credentialResponse) => {
      try {
        const response = await createLeadWithOauth({
          id_token: credentialResponse.credential,
          provider: GOOGLE_PROVIDER,
          form_location: FORM_LOCATION,
          lead_type: LEAD_TYPE,
          newsletter_ids: newsletterIds,
          opt_in_required: isGdprRequired,
          declined_opt_in: isGdprRequired,
          page: window.location.pathname,
          redirect_to_on_success: `${redirectBasePath}?return_to=${encodeURIComponent(returnTo)}`,
        });
        const separator = response.location.includes('?') ? '&' : '?';
        window.location.href = `${response.location}${separator}new=${response.isNewUser}`;
      } catch {
        // Silent fail — One Tap is ambient, errors should not interrupt reading
      }
    },
    onError: () => {
      // Silent fail
    },
  });

  return null;
}

export default function GoogleOneTap(props: GoogleOneTapProps) {
  const hydrated = useServerSideRendering();
  if (!hydrated) return null;

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <GoogleOneTapInner {...props} />
    </GoogleOAuthProvider>
  );
}
